import { useMemo, useState } from 'react';
import { useEffect, useRef } from 'react';
import './adminDetails.css';
import CardTemplate from '../CardTemplate/CardTemplate';
import Tabs from '../Tabs/Tabs';
import InfoCardSmall from '../InfoCardSmall/InfoCardSmall';
import AdminMedList from '../AdminMedList/AdminMedList';
import { instance } from '../../api/api';
import DriverCard from '../DriverCard/DriverCard';
import AutoInfoCard from '../AutoInfoCard/AutoInfoCard';
import AdminRoadLists from '../AdminRoadLists/AdminRoadLists';
import TechTOLists from '../TechTOLists/TechTOLists';
import Spiner from '../Spiner/Spiner';
import { IUserBase, ITabsData, IMedic, ItechnikUser, IDriverUser } from '../../types/type';
import DriverTechCheckList from '../DriverTechCheckList/DriverTechCheckList';
import DriverMedList from '../DriverMedList/DriverMedList';
import DriverRoadLists from '../DriverRoadLists/DriverRoadLists';
import DriverAutoparks from '../DriverAutoparks/DriverAutoparks';
import MedicAutoparks from '../MedicAutoparks/MedicAutoparks';
import TechnicAutoparks from '../TechnicAutoparks/TechnicAutoparks';
import { DRIVER_NOT_REGISTERED_MESSAGE } from '../../utils/driverRegistration';
import { UserService } from '../../services/user.service';

interface ISelectedUser {
  id: string;
  role: string;
}

function AdminDetails({
  selectedUser,
  onUserClick,
}: {
  selectedUser: ISelectedUser;
  onUserClick: (id: string, role: string) => void;
}) {
  const [user, setUser] = useState<IUserBase>();
  // Для водителя: см. IDriverDetails в user.service
  const [driverState, setDriverState] = useState({ registered: false, hasDriverData: false });
  const [loadError, setLoadError] = useState('');
  const componentRef = useRef<HTMLDivElement | null>(null);

  const { id, role } = selectedUser;

  useEffect(() => {
    const fetchUser = async () => {
      setUser(undefined);
      setLoadError('');
      try {
        if (selectedUser.role === 'driver') {
          const { user: driverUser, ...state } = await UserService.getDriverDetails(selectedUser.id);
          setDriverState(state);
          setUser(driverUser);
          return;
        }

        // Для ролей админа и автопарка используем другой эндпоинт
        const isProfileOnly = selectedUser.role === 'admin' || selectedUser.role === 'autopark_employee';
        const url = isProfileOnly
          ? `/profiles/${selectedUser.id}`
          : `/profiles/${selectedUser.role}/${selectedUser.id}`;
        const response = await instance.get<IUserBase>(url);
        setUser(response.data);

        // else if (selectedUser.role == 'technik') {
        //     const { profile, ...otherData } = response.data;
        //     const technik = { ...otherData, ...profile };

        //     setUser(technik);
        //   } else if (selectedUser.role == 'medic') {
        //     const { profile, ...otherData } = response.data;
        //     const medic = { ...otherData, ...profile };

        //     setUser(medic);
        //   } else {
        //     setUser(response.data);
        //   }
        // }
      } catch (error) {
        console.error('Ошибка при загрузке пользователя:', error);
        setLoadError('Не удалось загрузить пользователя');
      }
    };
    fetchUser();
  }, [id, role]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
        onUserClick('', '');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const tabs: ITabsData[] = useMemo(() => {
    if (user?.role == 'medic') {
      const medic = user as IMedic;
      return [
        {
          id: 1,
          name: 'Профиль',
          title: 'Профиль',
          component: <InfoCardSmall user={medic} />,
          style: '',
        },
        {
          id: 2,
          name: 'Мед. осмотры',
          title: 'Мед. осмотры',
          component: <AdminMedList user={medic} />,
          style: '',
        },
        {
          id: 3,
          name: 'Автопарки',
          title: 'Автопарки',
          component: <MedicAutoparks medic={medic} />,
          style: '',
        },
      ];
    } else if (user?.role == 'technik') {
      const otherUser = user as ItechnikUser;
      return [
        {
          id: 1,
          name: 'Профиль',
          title: 'Профиль',
          component: <InfoCardSmall user={otherUser} />,
          style: '',
        },
        {
          id: 2,
          name: 'ТО',
          title: 'ТО',
          component: <TechTOLists data={otherUser} />,
          style: '',
        },
        {
          id: 3,
          name: 'Автопарки',
          title: 'Автопарки',
          component: <TechnicAutoparks technic={otherUser} />,
          style: '',
        },
      ];
    } else if (user?.role == 'driver' && !driverState.hasDriverData) {
      // Водитель ещё не заполнил данные: только профиль, вкладок водителя нет
      return [
        {
          id: 1,
          name: 'Профиль',
          title: 'Профиль',
          component: (
            <>
              <div className="admin-details__notice">{DRIVER_NOT_REGISTERED_MESSAGE}</div>
              <InfoCardSmall user={user} registered={driverState.registered} />
            </>
          ),
          style: '',
        },
      ];
    } else if (user?.role == 'driver') {
      const driverUser = user as IDriverUser;
      return [
        {
          id: 1,
          name: 'Профиль',
          title: 'Профиль',
          component: <InfoCardSmall user={driverUser} registered={driverState.registered} />,
          style: '',
        },
        {
          id: 2,
          name: 'Данные водителя',
          title: 'Данные водителя',
          component: <DriverCard user={driverUser} />,
          style: '',
        },
        {
          id: 3,
          name: 'Данные авто',
          title: 'Данные авто',
          component: <AutoInfoCard user={driverUser} />,
          style: '',
        },
        {
          id: 4,
          name: 'Путевые листы',
          title: 'Путевые листы',
          component: <DriverRoadLists driver={driverUser} />,
          style: '',
        },
        {
          id: 5,
          name: 'Медосмотры',
          title: 'Медосмотры',
          component: <DriverMedList driver={driverUser} />,
          style: '',
        },
        {
          id: 6,
          name: 'ТО',
          title: 'ТО',
          component: <DriverTechCheckList driver={driverUser} />,
          style: '',
        },
        {
          id: 7,
          name: 'Автопарки',
          title: 'Автопарки',
          component: <DriverAutoparks driver={driverUser} />,
          style: '',
        },
      ];
    } else if (user?.role == 'admin' || user?.role == 'autopark_employee') {
      return [
        {
          id: 1,
          name: 'Профиль',
          title: 'Профиль',
          component: <InfoCardSmall user={user} />,
          style: '',
        },
      ];
    }
    return [];
  }, [user, driverState]);

  if (loadError) {
    return (
      <div ref={componentRef}>
        <CardTemplate onUserClick={onUserClick}>
          <div className="admin-details__notice">{loadError}</div>
        </CardTemplate>
      </div>
    );
  }

  if (tabs.length === 0) {
    return (
      <div ref={componentRef}>
        <Spiner />
      </div>
    );
  }

  return (
    <div ref={componentRef}>
      <CardTemplate onUserClick={onUserClick}>
        <Tabs tabsData={tabs} />
      </CardTemplate>
    </div>
  );
}

export default AdminDetails;
